import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import GlobalExposure from '../../components/seo/GlobalExposure';
import MarketingTools from '../../components/seo/MarketingTools';
import InternationalSEO from '../../components/seo/InternationalSEO';

export default function Marketing() {
  const globalTactics = [
    {
      category: "Search Engine Marketing",
      icon: "ri-search-line",
      tactics: [
        "Multi-language SEO optimization for 15+ languages",
        "Google Ads campaigns in 25+ countries", 
        "Bing Ads for international markets",
        "Local search optimization for major cities",
        "Voice search optimization for travel queries"
      ]
    },
    {
      category: "Content Marketing",
      icon: "ri-article-line", 
      tactics: [
        "Travel destination guides for 200+ cities",
        "Flight booking tutorials in multiple languages",
        "Travel tips and industry insights blog",
        "Video content for YouTube and social media",
        "Infographics for travel statistics and trends"
      ]
    },
    {
      category: "Social Media Strategy",
      icon: "ri-share-line",
      tactics: [
        "Instagram travel photography campaigns",
        "Facebook targeted advertising by demographics",
        "LinkedIn B2B corporate travel outreach",
        "TikTok short-form travel content",
        "Pinterest travel inspiration boards"
      ]
    },
    {
      category: "Partnership Marketing",
      icon: "ri-team-line",
      tactics: [
        "Travel blogger collaboration programs",
        "Influencer partnerships in key markets",
        "Affiliate marketing with travel websites",
        "Hotel and tourism board partnerships",
        "Corporate travel program partnerships"
      ]
    },
    {
      category: "Email Marketing",
      icon: "ri-mail-line",
      tactics: [
        "Personalized flight deal newsletters",
        "Geographic segmentation by region",
        "Behavioral trigger email campaigns",
        "Multi-language email templates",
        "Automated booking reminder sequences"
      ]
    },
    {
      category: "Public Relations",
      icon: "ri-megaphone-line",
      tactics: [
        "Press releases for travel industry publications",
        "Media outreach for travel journalists",
        "Conference speaking opportunities",
        "Award submissions and recognition programs",
        "Crisis communication and reputation management"
      ]
    }
  ];

  const competitorAnalysis = [
    {
      competitor: "Expedia",
      marketShare: "23%",
      strengths: ["Brand recognition", "Global presence"],
      opportunities: ["Personalized service", "Transparent pricing"]
    },
    {
      competitor: "Booking.com", 
      marketShare: "19%",
      strengths: ["User reviews", "Mobile app"],
      opportunities: ["Expert consultation", "Better fees"]
    },
    {
      competitor: "Kayak",
      marketShare: "15%",
      strengths: ["Price comparison", "Search functionality"], 
      opportunities: ["Human support", "Service quality"]
    },
    {
      competitor: "Priceline",
      marketShare: "12%",
      strengths: ["Bidding system", "Deals"],
      opportunities: ["Premium service", "Reliability"]
    }
  ];

  const marketingBudget = [
    { channel: "Search Engine Marketing", budget: "$125,000", percentage: 35 },
    { channel: "Social Media Advertising", budget: "$89,000", percentage: 25 },
    { channel: "Content Creation", budget: "$54,000", percentage: 15 },
    { channel: "Influencer Partnerships", budget: "$43,000", percentage: 12 },
    { channel: "Email Marketing", budget: "$25,000", percentage: 7 },
    { channel: "PR & Events", budget: "$21,000", percentage: 6 }
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <InternationalSEO 
        page="marketing"
        title="Global Marketing Strategy | MAVESKY TRAVELS Worldwide Expansion"
        description="Discover MAVESKY TRAVELS global marketing strategy for worldwide travel industry expansion. Comprehensive approach to building international brand recognition and customer acquisition across major travel markets worldwide."
        keywords="global marketing strategy, travel industry marketing, worldwide expansion, international marketing, travel business growth, marketing tactics, brand recognition, customer acquisition, travel marketing, MAVESKY TRAVELS marketing"
        canonicalUrl="https://maveskytravels.com/marketing"
      />
      
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Global <strong>Marketing Strategy</strong>
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
              Comprehensive approach to building <strong>worldwide brand recognition</strong> and 
              <strong>customer acquisition</strong> across all major <strong>travel markets</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                View Campaign Results
              </button>
              <button className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Download Strategy Guide
              </button>
            </div>
          </div>
        </section>

        {/* Marketing Tactics */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">Worldwide Exposure Tactics</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Strategic marketing approaches designed to maximize visibility and engagement across global markets.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              {globalTactics.map((category, index) => (
                <div key={index} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg mr-4">
                      <i className={`${category.icon} text-xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-white">{category.category}</h3>
                  </div>
                  
                  <ul className="space-y-3">
                    {category.tactics.map((tactic, tacticIndex) => (
                      <li key={tacticIndex} className="flex items-start text-gray-300">
                        <div className="w-5 h-5 flex items-center justify-center mr-3 mt-0.5">
                          <i className="ri-arrow-right-line text-blue-400 text-sm"></i>
                        </div>
                        <span className="text-sm">{tactic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Competitor Analysis */}
        <section className="py-20 bg-gray-800/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">Competitive Landscape</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Understanding our position in the global travel booking market and identifying opportunities for growth.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {competitorAnalysis.map((competitor, index) => (
                <div key={index} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-white">{competitor.competitor}</h3>
                    <div className="text-blue-400 font-semibold text-lg">{competitor.marketShare}</div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-white font-medium mb-2">Their Strengths</h4>
                      <ul className="space-y-1">
                        {competitor.strengths.map((strength, strengthIndex) => (
                          <li key={strengthIndex} className="text-gray-400 text-sm flex items-center">
                            <div className="w-4 h-4 flex items-center justify-center mr-2">
                              <i className="ri-shield-line text-orange-400"></i>
                            </div>
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-white font-medium mb-2">Our Advantages</h4>
                      <ul className="space-y-1">
                        {competitor.opportunities.map((opportunity, oppIndex) => (
                          <li key={oppIndex} className="text-gray-400 text-sm flex items-center">
                            <div className="w-4 h-4 flex items-center justify-center mr-2">
                              <i className="ri-star-line text-green-400"></i>
                            </div>
                            {opportunity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Marketing Budget Allocation */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-6">Marketing Investment Strategy</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Strategic budget allocation across channels for maximum ROI and global market penetration.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-white mb-8">Annual Marketing Budget</h3>
                <div className="space-y-4">
                  {marketingBudget.map((item, index) => (
                    <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-white font-medium">{item.channel}</span>
                        <span className="text-blue-400 font-semibold">{item.budget}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                      <div className="text-right text-gray-400 text-sm mt-1">{item.percentage}%</div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 bg-gradient-to-r from-green-900/30 to-blue-900/30 rounded-lg p-6 border border-green-800/50">
                  <h4 className="text-white font-semibold mb-2">Total Annual Investment</h4>
                  <div className="text-3xl font-bold text-green-400 mb-2">$357,000</div>
                  <p className="text-gray-300 text-sm">Projected 324% ROI based on current performance metrics</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-white mb-8">Growth Projections</h3>
                
                <div className="space-y-6">
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold">Market Expansion</h4>
                      <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg">
                        <i className="ri-global-line text-white"></i>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-blue-400 mb-2">15 New Markets</div>
                    <p className="text-gray-400 text-sm">Target expansion into 15 additional countries by Q4 2024</p>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold">Customer Acquisition</h4>
                      <div className="w-8 h-8 flex items-center justify-center bg-green-600 rounded-lg">
                        <i className="ri-user-add-line text-white"></i>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-green-400 mb-2">+185% Growth</div>
                    <p className="text-gray-400 text-sm">Projected customer base growth through strategic marketing</p>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white font-semibold">Revenue Impact</h4>
                      <div className="w-8 h-8 flex items-center justify-center bg-purple-600 rounded-lg">
                        <i className="ri-money-dollar-circle-line text-white"></i>
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-purple-400 mb-2">$4.2M Target</div>
                    <p className="text-gray-400 text-sm">Annual revenue target from global marketing initiatives</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Global Exposure Components */}
        <GlobalExposure />
        <MarketingTools />

        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Scale Globally?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Our proven marketing strategies have helped us reach travelers in over 50 countries. 
              Let's expand your travel business worldwide together.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Start Your Campaign
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Schedule Consultation
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
